import React, { useState, useEffect } from 'react';
import { View, ScrollView, StyleSheet, TextInput, TouchableOpacity, Alert, ActivityIndicator, Keyboard, Image, Platform } from 'react-native';
import * as ImagePicker from 'expo-image-picker';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { useTheme } from '../../theme/ThemeContext';
import AppShell from '../../components/layout/AppShell';
import ThemedText from '../../components/common/ThemedText';
import SearchableDropdown from '../../components/common/SearchableDropdown';
import { ClubsStackParamList } from '../../types/navigation';
import { joinClub, getClubById } from '../../lib/api/clubs';
import { useAuth } from '../../context/AuthContext';
import { INDUSTRIES } from '../../data/industries';
import { storage } from '../../utils/storage';
import { apiBaseUrl } from '../../config/env';
import { showSuccessToast } from '../../utils/toast';

type JoinClubFormNavigationProp = StackNavigationProp<ClubsStackParamList, 'JoinClubForm'>;
type JoinClubFormRouteProp = RouteProp<ClubsStackParamList, 'JoinClubForm'>;

const JoinClubFormScreen: React.FC = () => {
  const { theme } = useTheme();
  const navigation = useNavigation<JoinClubFormNavigationProp>();
  const route = useRoute<JoinClubFormRouteProp>();
  const { user } = useAuth();
  const { clubId, clubName } = route.params;

  const [loading, setLoading] = useState(false);
  const [allUsers, setAllUsers] = useState<{ id: string; name: string; subtitle?: string }[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [keyboardVisible, setKeyboardVisible] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [formData, setFormData] = useState({
    aboutBusiness: '',
    industry: user?.industry || '',
    referredBy: '',
    referredById: '',
    phone: user?.phoneNumber || '',
    socialNetworkId: '',
    contactSize: '',
    contactSphere: '',
    salesTurnover: '',
    businessPlace: '',
    cityResidentFrom: '',
    whyJoining: '',
    link: '',
    requestMessage: '',
    idProof: '',
  });

  useEffect(() => {
    loadAllUsers();
    
    const keyboardDidShowListener = Keyboard.addListener('keyboardDidShow', () => {
      setKeyboardVisible(true);
    });
    const keyboardDidHideListener = Keyboard.addListener('keyboardDidHide', () => {
      setKeyboardVisible(false);
    });

    return () => {
      keyboardDidShowListener.remove();
      keyboardDidHideListener.remove();
    };
  }, []);

  const loadAllUsers = async () => {
    try {
      const club = await getClubById(clubId);
      const membersList = (club.members || []).map((m) => ({
        id: m.userId,
        name: m.user?.name || 'Unknown',
        subtitle: m.user?.profession || undefined,
      }));
      setAllUsers(membersList);
    } catch (error) {
      console.error('Error loading club members:', error);
    } finally {
      setLoadingUsers(false);
    }
  };

  const updateField = (field: keyof typeof formData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const uploadImageToServer = async (uri: string): Promise<string> => {
    const token = await storage.getToken();
    if (!token) {
      throw new Error('No authentication token');
    }

    const formData = new FormData();
    const filename = uri.split('/').pop() || 'id_proof.jpg';
    const match = /\.(\w+)$/.exec(filename);
    const type = match ? `image/${match[1]}` : 'image/jpeg';

    // Handle web differently
    if (Platform.OS === 'web') {
      const response = await fetch(uri);
      const blob = await response.blob();
      const file = new File([blob], filename, { type });
      formData.append('image', file);
    } else {
      formData.append('image', {
        uri,
        name: filename,
        type,
      } as any);
    }

    const response = await fetch(`${apiBaseUrl}/api/upload/image`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    });

    const data = await response.json();
    
    if (!data.success || !data.url) {
      throw new Error(data.message || 'Failed to upload image');
    }

    return data.url;
  };

  const pickImage = async () => {
    try {
      // Request permission
      // Photo picker on Android 13+ uses system UI - no READ_MEDIA_* permissions needed

      // Pick image
      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        allowsEditing: true,
        aspect: [4, 3],
        quality: 0.8,
      });

      if (!result.canceled && result.assets[0]) {
        setUploadingImage(true);
        try {
          // Upload to cloud storage
          const cloudUrl = await uploadImageToServer(result.assets[0].uri);
          updateField('idProof', cloudUrl);
          Alert.alert('Success', 'ID proof uploaded successfully');
        } catch (error: any) {
          console.error('Error uploading image:', error);
          Alert.alert('Upload Failed', error.message || 'Failed to upload ID proof');
        } finally {
          setUploadingImage(false);
        }
      }
    } catch (error) {
      console.error('Error picking image:', error);
      Alert.alert('Error', 'Failed to pick image');
      setUploadingImage(false);
    }
  };

  const handleSubmit = async () => {
    // Validate required fields
    if (!formData.aboutBusiness.trim()) {
      Alert.alert('Required Field', 'Please provide information about your business');
      return;
    }
    if (!formData.phone.trim()) {
      Alert.alert('Required Field', 'Please provide your phone number');
      return;
    }
    if (!formData.referredBy.trim()) {
      Alert.alert('Required Field', 'Please select who referred you to this club');
      return;
    }

    try {
      setLoading(true);
      await joinClub(clubId, formData);
      showSuccessToast('Join request sent successfully!');
      navigation.goBack();
    } catch (error: any) {
      Alert.alert('Error', error.message || 'Failed to send join request');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AppShell title={`Join ${clubName}`} showBackButton scrollable={false}>
      <ScrollView style={styles.container} contentContainerStyle={styles.contentContainer}>
        <ThemedText style={[styles.subtitle, { color: theme.textSecondary }]}>
          Fill in the details to join this club
        </ThemedText>

        {/* About your Business - Required */}
        <View style={styles.fieldContainer}>
          <ThemedText style={[styles.label, { color: theme.text }]}>
            About your Business<ThemedText style={styles.required}>*</ThemedText>
          </ThemedText>
          <TextInput
            style={[styles.input, { backgroundColor: theme.card, color: theme.text, borderColor: theme.border }]}
            value={formData.aboutBusiness}
            onChangeText={(value) => updateField('aboutBusiness', value)}
            placeholder="Describe your business"
            placeholderTextColor={theme.textTertiary}
            multiline
            numberOfLines={3}
          />
        </View>

        {/* Industry - Searchable Dropdown */}
        <SearchableDropdown
          data={INDUSTRIES}
          value={formData.industry}
          onSelect={(item) => updateField('industry', typeof item === 'string' ? item : item.name)}
          placeholder="Select industry"
          label="Industry"
        />

        {/* Referred By - Any Member */}
        <SearchableDropdown
          data={allUsers}
          value={formData.referredBy}
          onSelect={(item) => {
            if (typeof item === 'string') {
              updateField('referredBy', item);
            } else {
              updateField('referredBy', item.name);
              setFormData(prev => ({ ...prev, referredById: item.id }));
            }
          }}
          placeholder="Select any member"
          label="Referred By *"
          disabled={loadingUsers}
        />

        {/* Phone Number - Required & Prefilled */}
        <View style={styles.fieldContainer}>
          <ThemedText style={[styles.label, { color: theme.text }]}>
            Phone Number<ThemedText style={styles.required}>*</ThemedText>
          </ThemedText>
          <TextInput
            style={[styles.input, { backgroundColor: theme.card, color: theme.text, borderColor: theme.border }]}
            value={formData.phone}
            onChangeText={(value) => updateField('phone', value)}
            placeholder="Your phone number"
            placeholderTextColor={theme.textTertiary}
            keyboardType="phone-pad"
          />
        </View>

        {/* Social Network ID - Optional */}
        <View style={styles.fieldContainer}>
          <ThemedText style={[styles.label, { color: theme.text }]}>
            Social Network ID <ThemedText style={[styles.optional, { color: theme.textTertiary }]}>(optional)</ThemedText>
          </ThemedText>
          <TextInput
            style={[styles.input, { backgroundColor: theme.card, color: theme.text, borderColor: theme.border }]}
            value={formData.socialNetworkId}
            onChangeText={(value) => updateField('socialNetworkId', value)}
            placeholder="LinkedIn, Twitter, etc."
            placeholderTextColor={theme.textTertiary}
          />
        </View>

        {/* Your Contact Size - Optional */}
        <View style={styles.fieldContainer}>
          <ThemedText style={[styles.label, { color: theme.text }]}>
            Your Contact Size <ThemedText style={[styles.optional, { color: theme.textTertiary }]}>(optional)</ThemedText>
          </ThemedText>
          <TextInput
            style={[styles.input, { backgroundColor: theme.card, color: theme.text, borderColor: theme.border }]}
            value={formData.contactSize}
            onChangeText={(value) => updateField('contactSize', value)}
            placeholder="Number of contacts"
            placeholderTextColor={theme.textTertiary}
            keyboardType="numeric"
          />
        </View>

        {/* Contact Sphere - Optional */}
        <View style={styles.fieldContainer}>
          <ThemedText style={[styles.label, { color: theme.text }]}>
            Contact Sphere <ThemedText style={[styles.optional, { color: theme.textTertiary }]}>(optional)</ThemedText>
          </ThemedText>
          <TextInput
            style={[styles.input, { backgroundColor: theme.card, color: theme.text, borderColor: theme.border }]}
            value={formData.contactSphere}
            onChangeText={(value) => updateField('contactSphere', value)}
            placeholder="Your network reach"
            placeholderTextColor={theme.textTertiary}
          />
        </View>

        {/* Sales Turnover - Optional */}
        <View style={styles.fieldContainer}>
          <ThemedText style={[styles.label, { color: theme.text }]}>
            Sales Turnover <ThemedText style={[styles.optional, { color: theme.textTertiary }]}>(optional)</ThemedText>
          </ThemedText>
          <TextInput
            style={[styles.input, { backgroundColor: theme.card, color: theme.text, borderColor: theme.border }]}
            value={formData.salesTurnover}
            onChangeText={(value) => updateField('salesTurnover', value)}
            placeholder="Annual sales turnover"
            placeholderTextColor={theme.textTertiary}
          />
        </View>

        {/* Business Place - Optional */}
        <View style={styles.fieldContainer}>
          <ThemedText style={[styles.label, { color: theme.text }]}>
            Business Place <ThemedText style={[styles.optional, { color: theme.textTertiary }]}>(optional)</ThemedText>
          </ThemedText>
          <TextInput
            style={[styles.input, { backgroundColor: theme.card, color: theme.text, borderColor: theme.border }]}
            value={formData.businessPlace}
            onChangeText={(value) => updateField('businessPlace', value)}
            placeholder="Business location"
            placeholderTextColor={theme.textTertiary}
          />
        </View>

        {/* City Resident From - Optional */}
        <View style={styles.fieldContainer}>
          <ThemedText style={[styles.label, { color: theme.text }]}>
            City Resident From <ThemedText style={[styles.optional, { color: theme.textTertiary }]}>(optional)</ThemedText>
          </ThemedText>
          <TextInput
            style={[styles.input, { backgroundColor: theme.card, color: theme.text, borderColor: theme.border }]}
            value={formData.cityResidentFrom}
            onChangeText={(value) => updateField('cityResidentFrom', value)}
            placeholder="Your city of residence"
            placeholderTextColor={theme.textTertiary}
          />
        </View>

        {/* Why You Joining This - Optional */}
        <View style={styles.fieldContainer}>
          <ThemedText style={[styles.label, { color: theme.text }]}>
            Why You Joining This <ThemedText style={[styles.optional, { color: theme.textTertiary }]}>(optional)</ThemedText>
          </ThemedText>
          <TextInput
            style={[styles.input, { backgroundColor: theme.card, color: theme.text, borderColor: theme.border }]}
            value={formData.whyJoining}
            onChangeText={(value) => updateField('whyJoining', value)}
            placeholder="Your reason for joining"
            placeholderTextColor={theme.textTertiary}
            multiline
            numberOfLines={3}
          />
        </View>

        {/* Link */}
        <View style={styles.fieldContainer}>
          <ThemedText style={[styles.label, { color: theme.text }]}>Link</ThemedText>
          <TextInput
            style={[styles.input, { backgroundColor: theme.card, color: theme.text, borderColor: theme.border }]}
            value={formData.link}
            onChangeText={(value) => updateField('link', value)}
            placeholder="Website or social media link"
            placeholderTextColor={theme.textTertiary}
            keyboardType="url"
            autoCapitalize="none"
          />
        </View>

        {/* ID Proof - Optional */}
        <View style={styles.fieldContainer}>
          <ThemedText style={[styles.label, { color: theme.text }]}>
            ID Proof <ThemedText style={[styles.optional, { color: theme.textTertiary }]}>(optional)</ThemedText>
          </ThemedText>
          {formData.idProof ? (
            <View style={styles.imagePreviewContainer}>
              <Image
                source={{ uri: formData.idProof }}
                style={styles.imagePreview}
                resizeMode="cover"
              />
              <TouchableOpacity
                style={[styles.changeImageButton, { backgroundColor: theme.accent }]}
                onPress={pickImage}
                disabled={uploadingImage}
              >
                <ThemedText style={styles.changeImageText}>
                  {uploadingImage ? 'Uploading...' : 'Change Image'}
                </ThemedText>
              </TouchableOpacity>
            </View>
          ) : (
            <TouchableOpacity
              style={[styles.uploadButton, { borderColor: theme.border, backgroundColor: theme.card }]}
              onPress={pickImage}
              disabled={uploadingImage}
            >
              <Ionicons name="cloud-upload-outline" size={32} color={theme.textSecondary} />
              <ThemedText style={[styles.uploadText, { color: theme.textSecondary }]}>
                {uploadingImage ? 'Uploading...' : 'Upload ID Proof Image'}
              </ThemedText>
            </TouchableOpacity>
          )}
        </View>

        {/* Request Message - Optional */}
        <View style={styles.fieldContainer}>
          <ThemedText style={[styles.label, { color: theme.text }]}>
            Write Your Request <ThemedText style={[styles.optional, { color: theme.textTertiary }]}>(optional)</ThemedText>
          </ThemedText>
          <TextInput
            style={[styles.input, styles.textArea, { backgroundColor: theme.card, color: theme.text, borderColor: theme.border }]}
            value={formData.requestMessage}
            onChangeText={(value) => updateField('requestMessage', value)}
            placeholder="Additional message or request"
            placeholderTextColor={theme.textTertiary}
            multiline
            numberOfLines={4}
          />
        </View>

        {/* Submit Button */}
        <TouchableOpacity
          style={[styles.submitButton, { backgroundColor: theme.accent }]}
          onPress={handleSubmit}
          disabled={loading}
        >
          {loading ? (
            <ActivityIndicator color={theme.background} />
          ) : (
            <ThemedText style={[styles.submitButtonText, { color: theme.background }]}>
              Submit Request
            </ThemedText>
          )}
        </TouchableOpacity>

        {/* Keyboard Spacer - Extra space when keyboard is visible */}
        {keyboardVisible && <View style={{ height: 250 }} />}
      </ScrollView>
    </AppShell>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  contentContainer: {
    padding: 16,
    paddingBottom: 32,
  },
  subtitle: {
    fontSize: 14,
    marginBottom: 24,
  },
  fieldContainer: {
    marginBottom: 20,
  },
  label: {
    fontSize: 14,
    fontWeight: '500',
    marginBottom: 8,
  },
  required: {
    color: '#FF4444',
  },
  optional: {
    fontSize: 12,
    fontWeight: '400',
  },
  input: {
    borderWidth: 1,
    borderRadius: 8,
    padding: 12,
    fontSize: 14,
  },
  textArea: {
    minHeight: 80,
    textAlignVertical: 'top',
  },
  submitButton: {
    borderRadius: 8,
    padding: 16,
    alignItems: 'center',
    marginTop: 16,
    marginBottom: 16,
  },
  submitButtonText: {
    fontSize: 16,
    fontWeight: '600',
  },
  uploadButton: {
    borderWidth: 2,
    borderStyle: 'dashed',
    borderRadius: 12,
    padding: 32,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 150,
  },
  uploadText: {
    marginTop: 12,
    fontSize: 14,
    fontWeight: '500',
  },
  imagePreviewContainer: {
    borderRadius: 12,
    overflow: 'hidden',
  },
  imagePreview: {
    width: '100%',
    height: 200,
    borderRadius: 12,
  },
  changeImageButton: {
    marginTop: 8,
    padding: 12,
    borderRadius: 8,
    alignItems: 'center',
  },
  changeImageText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '600',
  },
});

export default JoinClubFormScreen;
